'use client';

import { useMemo, useEffect, useCallback } from 'react';
import { useSetState } from 'src/hooks/use-set-state';
import { isValidToken, setSession } from './utils';
import { adminSignInWithPassword } from './action';
import { AuthContext } from '../auth-context';
import { STORAGE_KEY } from './constant';

export function AuthProvider({ children }) {
  const { state, setState } = useSetState({
    user: null,
    loading: true,
  });

  const checkUserSession = useCallback(() => {
    try {
      if (typeof window !== 'undefined') {
        const accessToken = localStorage.getItem(STORAGE_KEY);
        const storedUser = localStorage.getItem('admin_user');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          if (storedUser) {
            const user = JSON.parse(storedUser);
            setState({ user, loading: false });
            return;
          }
        }
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem('admin_user');
        setState({ user: null, loading: false });
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error('Session check error:', error);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  const signIn = useCallback(
    async (email, password) => {
      try {
        const { user, accessToken } = await adminSignInWithPassword({ email, password });
        setState({ user, loading: false });
        return { user };
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [setState]
  );

  const signOut = useCallback(() => {
    setSession(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('admin_user');
    }
    setState({ user: null, loading: false });
  }, [setState]);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';
  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            id: state.user?.id,
            email: state.user?.email,
            displayName: [state.user?.first_name, state.user?.last_name].filter(Boolean).join(' ') || state.user?.email,
            role: state.user?.role?.toLowerCase() || 'admin',
            avatarUrl: state.user?.profile_photo_url,
          }
        : null,
      signIn,
      signOut,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [signIn, signOut, checkUserSession, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
